import { WorkPolicySummary as WorkPolicySummaryView } from "../work-type-shared/work-policy-summary";
import {
  PolicyKind,
  WorkPolicySummary
} from "../../api/work-policy-api.types";
import {
  getCatalogStatus,
  PolicyCatalogError,
  PolicyCatalogFilters
} from "./hooks/use-work-policy-catalog";
import {
  CatalogActionButton,
  CatalogHeader,
  CatalogMeta,
  CatalogSurface,
  CatalogTitleRow,
  FilterGrid,
  PolicyButton,
  PolicyList,
  SearchClearButton,
  SearchField,
  StateMessage,
  TabButton,
  TabRow,
  TypeFilterButton,
  TypeFilterGroup,
  VisuallyHidden
} from "./work-type-management.styles";

interface PolicyCatalogProps {
  policyKind: PolicyKind;
  summaries: WorkPolicySummary[];
  totalCount: number;
  filters: PolicyCatalogFilters;
  loading: boolean;
  error: PolicyCatalogError | null;
  filtersActive: boolean;
  selectedId?: number;
  onKindChange: (kind: PolicyKind) => void;
  onFiltersChange: (filters: PolicyCatalogFilters) => void;
  onResetFilters: () => void;
  onSelect: (summary: WorkPolicySummary, trigger: HTMLButtonElement) => void;
  onCreate: () => void;
  onRetry: () => void;
  onSafeBack: () => void;
}

function summaryRule(summary: WorkPolicySummary) {
  const target =
    summary.policyKind === "APPROVAL" && summary.targetCount != null
      ? ` · 대상 ${summary.targetCount}개`
      : "";
  return `일 ${summary.dailyBaseHours}시간 · 주 ${summary.weeklyBaseHours}시간${target}`;
}

export const PolicyCatalog = ({
  policyKind,
  summaries,
  totalCount,
  filters,
  loading,
  error,
  filtersActive,
  selectedId,
  onKindChange,
  onFiltersChange,
  onResetFilters,
  onSelect,
  onCreate,
  onRetry,
  onSafeBack
}: PolicyCatalogProps) => (
  <CatalogSurface aria-label="등록된 근무정책">
    <CatalogHeader>
      <CatalogTitleRow>
        <div>
          <h2>등록된 근무정책</h2>
          <strong>총 {totalCount}개</strong>
        </div>
        <p>
          {policyKind === "APPROVAL"
            ? "결재에 노출할 정책"
            : "구성원의 기본 근무 기준"}
        </p>
      </CatalogTitleRow>
      <TabRow role="tablist" aria-label="정책 종류 탭">
        <TabButton
          type="button"
          role="tab"
          active={policyKind === "APPROVAL"}
          tone="approval"
          aria-selected={policyKind === "APPROVAL"}
          onClick={() => onKindChange("APPROVAL")}
        >
          결재 신청용
        </TabButton>
        <TabButton
          type="button"
          role="tab"
          active={policyKind === "BASE"}
          tone="active"
          aria-selected={policyKind === "BASE"}
          onClick={() => onKindChange("BASE")}
        >
          기본 정책
        </TabButton>
      </TabRow>
      <FilterGrid>
        <SearchField>
          <input
            type="search"
            aria-label="근무정책명 검색"
            placeholder="근무정책명 검색"
            value={filters.keyword}
            onChange={event =>
              onFiltersChange({ ...filters, keyword: event.currentTarget.value })
            }
          />
          {filters.keyword && (
            <SearchClearButton
              type="button"
              aria-label="근무정책명 검색 지우기"
              onClick={() => onFiltersChange({ ...filters, keyword: "" })}
            >
              ×
            </SearchClearButton>
          )}
        </SearchField>
        <TypeFilterGroup role="group" aria-label="근무 유형 필터">
          {([
            ["ALL", "전체"],
            ["staggered", "시차"],
            ["flexible", "탄력"],
            ["selective", "선택"]
          ] as const).map(([value, label]) => (
            <TypeFilterButton
              key={value}
              type="button"
              active={filters.workType === value}
              aria-pressed={filters.workType === value}
              onClick={() => onFiltersChange({ ...filters, workType: value })}
            >
              {label}
            </TypeFilterButton>
          ))}
        </TypeFilterGroup>
        {policyKind === "APPROVAL" && (
          <select
            aria-label="정책 상태 필터"
            value={filters.status}
            onChange={event =>
              onFiltersChange({
                ...filters,
                status: event.currentTarget.value as PolicyCatalogFilters["status"]
              })
            }
          >
            <option value="ALL">전체 상태</option>
            <option value="ACTIVE">사용 중</option>
            <option value="INACTIVE">미 사용</option>
            <option value="VISIBLE">결재 노출</option>
            <option value="HIDDEN">결재 숨김</option>
          </select>
        )}
      </FilterGrid>
    </CatalogHeader>
    <CatalogMeta>
      <strong aria-live="polite">현재 {summaries.length}개</strong>
      <span>
        <CatalogActionButton type="button" onClick={onResetFilters} disabled={!filtersActive}>
          필터 초기화
        </CatalogActionButton>
        <CatalogActionButton
          type="button"
          primary
          aria-describedby="policy-create-description"
          onClick={onCreate}
        >
          신규 정책 추가
        </CatalogActionButton>
        <VisuallyHidden id="policy-create-description">
          현재 선택한 {policyKind === "APPROVAL" ? "결재 신청용" : "기본"} 정책을
          만듭니다.
        </VisuallyHidden>
      </span>
    </CatalogMeta>
    {loading ? (
      <StateMessage role="status">
        <div>
          <strong>근무정책을 불러오는 중입니다.</strong>
          <p>등록된 정책과 상태를 확인하고 있습니다.</p>
        </div>
      </StateMessage>
    ) : error ? (
      <StateMessage role="alert">
        <div>
          <strong>
            {error.code === "WORK_POLICY_FORBIDDEN"
              ? "근무정책 관리 권한이 필요합니다"
              : error.code === "WORK_POLICY_DB_DEPENDENCY"
              ? "서비스 준비가 필요합니다"
              : "근무정책을 불러오지 못했습니다"}
          </strong>
          <p>{error.message}</p>
          <CatalogActionButton type="button" onClick={error.retryable ? onRetry : onSafeBack}>
            {error.retryable ? "목록 다시 확인" : "접근 가능한 화면으로 돌아가기"}
          </CatalogActionButton>
        </div>
      </StateMessage>
    ) : summaries.length === 0 ? (
      <StateMessage role="status">
        <div>
          <strong>
            {filtersActive ? "검색 결과가 없습니다" : "등록된 정책이 없습니다"}
          </strong>
          <p>
            {filtersActive
              ? "검색어나 필터를 바꿔 다른 정책을 찾아보세요."
              : "신규 정책 추가로 첫 근무정책을 등록할 수 있습니다."}
          </p>
        </div>
      </StateMessage>
    ) : (
      <PolicyList role="list" aria-label="정책 목록" tabIndex={0}>
        {summaries.map(summary => (
          <PolicyButton
            key={summary.workPolicyId}
            type="button"
            selected={selectedId === summary.workPolicyId}
            aria-label={`${summary.profileName} ${
              selectedId === summary.workPolicyId ? "선택됨" : "선택"
            }`}
            aria-pressed={selectedId === summary.workPolicyId}
            onClick={event => onSelect(summary, event.currentTarget)}
          >
            <WorkPolicySummaryView
              variant="admin"
              appearance="modern"
              selected={selectedId === summary.workPolicyId}
              policy={{
                id: String(summary.workPolicyId),
                name: summary.profileName,
                policyKind: summary.policyKind,
                workType: summary.workType,
                status: getCatalogStatus(summary),
                exposureStatus: summary.exposureStatus ?? undefined,
                ruleSummary: summaryRule(summary)
              }}
            />
          </PolicyButton>
        ))}
      </PolicyList>
    )}
  </CatalogSurface>
);
